import React from "react";
import ReactSelect from "react-select";
import tw, { theme } from "twin.macro";
import isEmpty from "lodash/isEmpty";
import get from "lodash/get";
import classNames from "classnames";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

import { Checkbox } from "components/ui";
import Spinner from "../Spinner";
import { useConfig } from "../ConfigProvider";
import { useForm } from "../Form/context";
import { useInputGroup } from "../InputGroup/context";

import { HiChevronDown, HiX } from "react-icons/hi";

import { CONTROL_SIZES, SIZES } from "../utils/constant";

const DefaultCheckboxOption = ({
  innerProps,
  label,
  selectProps,
  isSelected,
  isDisabled,
  isFocused,
}) => {
  const { t } = useTranslation();

  const newInnerProps = {
    ...innerProps,
    onClick: function onClick(event) {
      event.stopPropagation();
      innerProps.onClick(event);
    },
  };

  return (
    <div
      className={classNames(
        "select-option",
        isDisabled && "disabled",
        isFocused && "focused",
      )}
      {...newInnerProps}
    >
      <Checkbox className="ml-2" checked={isSelected}>
        <p>{t(label)}</p>
      </Checkbox>
    </div>
  );
};

const DefaultOption = ({
  innerProps,
  label,
  selectProps,
  isDisabled,
  isFocused,
}) => {
  const { t } = useTranslation();

  const newInnerProps = {
    ...innerProps,
    onClick: function onClick(event) {
      event.stopPropagation();
      innerProps.onClick(event);
    },
  };

  return (
    <div
      className={classNames(
        "select-option",
        isDisabled && "disabled",
        isFocused && "focused",
      )}
      {...newInnerProps}
    >
      <p>{t(label)}</p>
    </div>
  );
};

const MultiValue = ({ data, removeProps, components }) => {
  const { t } = useTranslation();

  return (
    <div className="select__multi-value m-0.5 flex items-center">
      <div className="select__multi-value__label p-1 pl-1.5 text-[85%] truncate rounded-sm">
        {t(data?.label)}
      </div>
      <div
        className="select__multi-value__remove px-1 flex text-center rounded-sm [&>div]:flex"
        aria-label={`Remove ${data?.label}`}
        {...removeProps}
      >
        <components.Remove {...removeProps} />
      </div>
    </div>
  );
};

const DefaultDropdownIndicator = () => {
  return (
    <div className="select-dropdown-indicator">
      <HiChevronDown />
    </div>
  );
};

const DefaultClearIndicator = (props) => {
  const {
    innerProps: { ref, ...restInnerProps },
  } = props;
  return (
    <div {...restInnerProps} ref={ref}>
      <div className="select-clear-indicator">
        <HiX />
      </div>
    </div>
  );
};

const DefaultLoadingIndicator = ({ selectProps }) => {
  const { themeColor } = selectProps;
  return <Spinner className={`select-loading-indicatior text-${themeColor}`} />;
};

const Select = React.forwardRef((props, ref) => {
  const {
    size,
    style,
    className,
    form,
    field,
    components,
    checkboxOption = false,
    componentAs: Component,
    ...rest
  } = props;

  const { themeColor, controlSize, primaryColorLevel, mode } = useConfig();
  const formControlSize = useForm()?.size;
  const inputGroupSize = useInputGroup()?.size;

  const selectSize = size || inputGroupSize || formControlSize || controlSize;

  const twColor = theme`colors`;
  const twHeight = theme`height`;

  let isInvalid = false;

  if (!isEmpty(form)) {
    const { touched, errors } = form;

    const touchedField = get(touched, field.name);
    const errorField = get(errors, field.name);

    isInvalid = touchedField && errorField;
  }

  const getBoxShadow = (state) => {
    const shadaowBase = "0 0 0 1px ";

    if (isInvalid) {
      return shadaowBase + twColor.red["500"];
    }

    if (state.isFocused) {
      return shadaowBase + twColor[themeColor][primaryColorLevel];
    }

    return "none";
  };

  const styles = {
    control: (provided, state) => {
      return {
        ...provided,
        height: twHeight[CONTROL_SIZES[selectSize]],
        minHeight: twHeight[CONTROL_SIZES[selectSize]],
        "&:hover": {
          boxShadow: getBoxShadow(state),
          cursor: "pointer",
        },
        boxShadow: getBoxShadow(state),
        borderRadius: tw`rounded-md`.borderRadius,
        ...(isInvalid ? { borderColor: twColor.red["500"] } : {}),
      };
    },
    input: (css) => {
      return {
        ...css,
        input: {
          outline: "none",
          outlineOffset: 0,
          boxShadow: "none !important",
        },
      };
    },
    valueContainer: (provided) => ({
      ...provided,
      maxHeight: "120px",
      overflowY: "auto",
      msOverflowStyle: "none",
      "&::-webkit-scrollbar": {
        display: "none",
      },
    }),
    menu: (provided) => ({ ...provided, zIndex: 50 }),
    ...style,
  };

  const selectClass = classNames("select", `select-${selectSize}`, className);

  return (
    <Component
      className={selectClass}
      classNamePrefix={"select"}
      ref={ref}
      styles={styles}
      theme={(theme) => ({
        ...theme,
        colors: {
          ...theme.colors,
          neutral20:
            mode === "dark" ? twColor.gray["600"] : twColor.gray["300"],
          neutral30:
            mode === "dark" ? twColor.gray["600"] : twColor.gray["300"],
          neutral80: twColor.gray["700"],
          neutral10:
            mode === "dark" ? twColor.gray["600"] : twColor.gray["300"],
          primary25: twColor[themeColor]["50"],
          primary50: twColor[themeColor]["100"],
          primary: twColor[themeColor][primaryColorLevel],
        },
      })}
      themeColor={`${themeColor}-${primaryColorLevel}`}
      components={{
        IndicatorSeparator: () => null,
        Option: checkboxOption ? DefaultCheckboxOption : DefaultOption,
        LoadingIndicator: DefaultLoadingIndicator,
        DropdownIndicator: DefaultDropdownIndicator,
        ClearIndicator: DefaultClearIndicator,
        MultiValue,
        ...components,
      }}
      {...field}
      {...rest}
    />
  );
});

Select.propTypes = {
  size: PropTypes.oneOf([SIZES.LG, SIZES.MD, SIZES.SM]),
  componentAs: PropTypes.elementType,
};

Select.defaultProps = {
  componentAs: ReactSelect,
};

export default Select;
